import React from 'react';

import PropTypes from 'prop-types';
import { HiOutlineBackspace } from 'react-icons/hi';
import { useHistory, useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

import { formatCurrency, parseNumber } from 'modules/common/utils';
import { Button } from 'modules/common/input';
import { toastWarning } from 'modules/common/toast';
import { transformGetSelectedRecentTransactions } from 'modules/branch/services-management/pos-management/recent-transactions/transformers';

import { useGetDiscount, useGetCart, usePutQuickCheckout } from '../../hooks';
import PrintReceiptV2 from './PrintReceiptV2';

const OPTIONS = [
  {
    value: 100,
    method: '+',
  },
  {
    value: 500,
    method: '+',
  },
  {
    value: 1000,
    method: '+',
  },
];

const DEFAULT_FORM = {
  amount_tendered: '',
};

function Cash({
  onParentClose,
  parentMatch,
  branch,
  withGoBack,
  onGoBack,
  cartTotal,
  onSuccess,
  isProcessingInModal,
  data,
  type: $type,
  settings,
}) {
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const [receiptProps, setReceiptProps] = React.useState({});
  const { state } = useLocation() || {};

  const history = useHistory();

  const [isPutLoading, put] = usePutQuickCheckout();
  const [cart, setCart, includes] = useGetCart();
  const [discounts, setDiscounts] = useGetDiscount();

  const getTotal = cartTotal ?? state?.cartTotal;

  const handleOnClick =
    (v, type = 'concat') =>
    () => {
      setForm((prevState) => {
        let $v = {
          ...prevState,
          amount_tendered: `${prevState?.amount_tendered}${v}`,
        };

        if (v === 0 && prevState?.amount_tendered?.length <= 0) {
          $v = {
            ...prevState,
            amount_tendered: ``,
          };
        }

        if (v === 'clear') {
          $v = {
            ...prevState,
            amount_tendered: ``,
          };
        }

        if (type === 'add') {
          $v = {
            ...prevState,
            amount_tendered: `${
              parseFloat(prevState?.amount_tendered || 0) + v
            }`,
          };
        }

        if (v === 'exact_payment') {
          $v = {
            ...prevState,
            amount_tendered: `${getTotal}`,
          };
        }

        if (v === '.' && prevState?.amount_tendered?.includes('.')) {
          $v = {
            ...prevState,
            amount_tendered: prevState?.amount_tendered,
          };
        }

        if (v === 'backspace') {
          $v = {
            ...prevState,
            amount_tendered:
              prevState?.amount_tendered?.length > 0
                ? prevState?.amount_tendered?.slice(0, -1)
                : '',
          };
        }

        return $v;
      });
    };

  const handleOnComplete = () => {
    if (parseNumber(form?.amount_tendered) <= 0) {
      toastWarning('Amount Tendered is required.');
      return;
    }

    if (parseNumber(form?.amount_tendered || 0) < parseNumber(getTotal)) {
      toastWarning('Amount Tendered must be greater than equal to Total.');
      return;
    }

    const change =
      parseNumber(form?.amount_tendered || 0) - parseNumber(getTotal);
    const amount_tendered = form?.amount_tendered;

    const addOns = (row) => {
      const groups = [
        ...new Set(row?.addOns?.map((item) => item?.addon_group_uuid)),
      ];

      return {
        addons: groups?.reduce(
          (acc, item) => ({
            ...acc,
            [item]: row?.addOns
              ?.filter((subItem) => subItem?.addon_group_uuid === item)
              ?.map((subItem) => subItem?.uuid),
          }),
          {}
        ),
      };
    };

    const payload = {
      branch,
      cart: isEmpty(data)
        ? cart
        : data?.products?.map((item) => ({
            has_variant: Boolean(item?.product_variant_uuid),
            uuid: item?.product_uuid,
            selected_variant: {
              product_variant_uuid: item?.product_variant_uuid,
            },
            quantity: item?.quantity,
            ...(item?.addOns?.length > 0 && addOns(item)),
          })),
      amount_tendered,
      change,
    };

    put(
      {
        payload,
      },
      (res) => {
        const cb = () => {
          const { data: $data, included } = res?.response?.order_details || {};
          setCart([]);

          if (isProcessingInModal) {
            setReceiptProps({
              type: $type,
              includes,
              branch,
              state: {
                cart,
                payment_method: 'CASH',
                amount_tendered,
                change,
                res: transformGetSelectedRecentTransactions($data, included),
                settings: state?.settings,
                subtotal: state?.subtotal,
                discounts,
              },
            });

            setDiscounts({
              apply_discount: false,
              selected_orders: [],
              discount_type: [],
            });

            return;
          }

          history.push({
            pathname: `${parentMatch?.url}/receipt`,
            state: {
              cart,
              payment_method: 'CASH',
              amount_tendered,
              change,
              res: transformGetSelectedRecentTransactions($data, included),
              settings: state?.settings,
              subtotal: state?.subtotal,
              discounts,
            },
          });
          setDiscounts({
            apply_discount: false,
            selected_orders: [],
            discount_type: [],
          });
        };

        if (onSuccess) {
          onSuccess(cb);
          return;
        }

        cb();
      }
    );
  };

  if (!isEmpty(receiptProps)) {
    return (
      <PrintReceiptV2
        {...receiptProps}
        settings={settings}
        onGoBack={onGoBack}
        onParentClose={onParentClose}
      />
    );
  }

  return (
    <div className="flex flex-col flex-1 overflow-hidden">
      <div className="flex flex-col flex-1 items-center md:mt-7 p-3 md:p-5 overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col gap-3 w-full max-w-[700px]">
          <div className="space-y-2">
            <div className="font-semibold text-xl">Pay to order</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <div className="card !p-3 md:!p-5 bg-[#262225] text-white">
                <div className="text-xxs">TOTAL</div>
                <div className="text-xl font-semibold break-all">
                  {formatCurrency(parseNumber(getTotal) || 0, 'PHP', {
                    minimumFractionDigits: 2,
                  })}
                </div>
              </div>

              <div className="card !p-3 md:!p-5">
                <div className="text-xxs">AMOUNT TENDERED</div>
                <div className="text-xl font-semibold break-all">
                  {formatCurrency(
                    parseNumber(form?.amount_tendered) || 0,
                    'PHP',
                    {
                      minimumFractionDigits: 2,
                    }
                  )}
                </div>
              </div>

              <div className="card !p-3 md:!p-5 bg-gray-50">
                <div className="text-xxs">CHANGE</div>
                <div className="text-xl font-semibold break-all">
                  {parseNumber(form?.amount_tendered || 0) >=
                  parseNumber(getTotal)
                    ? formatCurrency(
                        parseNumber(form?.amount_tendered || 0) -
                          parseNumber(getTotal || 0),
                        'PHP',
                        {
                          minimumFractionDigits: 2,
                        }
                      )
                    : formatCurrency(0)}
                </div>
              </div>
            </div>
          </div>

          <div className="p-3 md:p-5 bg-gray-50">
            <div className="grid grid-cols-4 gap-1 md:gap-3">
              <div className="col-span-1 space-y-1 md:space-y-3">
                {OPTIONS?.map((item, key) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={key}
                    className="card p-1.5 md:p-3 text-center h-14 md:h-16 flex text-gray-500 cursor-pointer"
                    onClick={handleOnClick(item?.value, 'add')}
                    role="presentation"
                  >
                    <div className="m-auto text-xs md:text-base flex items-center gap-1">
                      <div className="-mt-0.5">{item?.method}</div>
                      <div>{formatCurrency(item?.value)}</div>
                    </div>
                  </div>
                ))}

                <div
                  className="card p-1.5 md:p-3 text-center h-14 md:h-16 flex text-gray-500 cursor-pointer "
                  onClick={handleOnClick('exact_payment')}
                  role="presentation"
                >
                  <div className="m-auto text-xxs md:text-sm">
                    Exact Payment
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 col-span-3 gap-1 md:gap-3">
                {[...new Array(9)]?.map((item, key) => (
                  <div
                    // eslint-disable-next-line react/no-array-index-key
                    key={key}
                    className="card p-1.5 md:p-3 text-center h-14 md:h-16 flex text-lg font-medium text-gray-600 cursor-pointer"
                    onClick={handleOnClick(key + 1)}
                    role="presentation"
                  >
                    <div className="m-auto">{key + 1}</div>
                  </div>
                ))}

                <div
                  className="card p-1.5 md:p-3 text-center h-14 md:h-16 flex text-lg font-medium text-gray-600 cursor-pointer"
                  onClick={handleOnClick('.')}
                  role="presentation"
                >
                  <div className="m-auto">.</div>
                </div>

                <div
                  className="card p-1.5 md:p-3 text-center h-14 md:h-16 flex text-lg font-medium text-gray-600 cursor-pointer"
                  onClick={handleOnClick(0)}
                  role="presentation"
                >
                  <div className="m-auto">0</div>
                </div>

                <div
                  className="card p-1.5 md:p-3 text-center h-14 md:h-16 flex text-lg font-medium text-gray-600 cursor-pointer"
                  onClick={handleOnClick('backspace')}
                  role="presentation"
                >
                  <div className="m-auto">
                    <HiOutlineBackspace className="h-6 w-6" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="gap-2 hidden md:flex">
            <Button
              className="btn primary font-semibold flex-1"
              label={cartTotal ? 'Complete Order' : 'Complete Transaction'}
              onClick={handleOnComplete}
              isLoading={isPutLoading}
              disabled={isPutLoading}
              withLoader
            />
            <Button
              className="btn outline-primary md:primary font-semibold"
              label="Clear"
              onClick={handleOnClick('clear')}
            />
            {withGoBack && (
              <Button
                className="btn outline-primary md:primary font-semibold"
                label="Go Back"
                onClick={onGoBack}
              />
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 p-3 md:!px-1.5 md:!pt-1 md:hidden">
        <Button
          className="btn outline-primary md:primary font-semibold flex-1"
          label="Complete Transaction"
          onClick={handleOnComplete}
          isLoading={isPutLoading}
          disabled={isPutLoading}
          withLoader
        />
        <Button
          className="btn outline-primary md:primary font-semibold"
          label="Clear"
          onClick={handleOnClick('clear')}
        />
      </div>
    </div>
  );
}

Cash.defaultProps = {
  parentMatch: {},
  branch: {},
  data: {},
  withGoBack: false,
  isProcessingInModal: false,
  onGoBack: () => {},
  onParentClose: () => {},
  onSuccess: false,
  type: 'BRANCH',
  cartTotal: null,
  settings: {},
};

Cash.propTypes = {
  type: PropTypes.string,
  parentMatch: PropTypes.instanceOf(Object),
  branch: PropTypes.instanceOf(Object),
  settings: PropTypes.instanceOf(Object),
  data: PropTypes.instanceOf(Object),
  withGoBack: PropTypes.bool,
  isProcessingInModal: PropTypes.bool,
  onGoBack: PropTypes.instanceOf(Function),
  onParentClose: PropTypes.instanceOf(Function),
  onSuccess: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  cartTotal: PropTypes.number,
};

export default Cash;
